import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../App';

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/login', form);
      login(data.token, data.user);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Λάθος στοιχεία');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      background: '#0A0F1E',
      overflow: 'hidden', position: 'relative'
    }}>
      {/* Animated grid background */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.06,
        backgroundImage: 'linear-gradient(rgba(201,147,10,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(201,147,10,0.8) 1px, transparent 1px)',
        backgroundSize: '48px 48px'
      }} />

      {/* Left panel — branding */}
      <div style={{
        width: '52%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '60px 64px', position: 'relative'
      }} className="hide-mobile">
        {/* Logo mark */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '12px',
            background: 'rgba(201,147,10,0.08)', border: '1px solid rgba(201,147,10,0.2)',
            borderRadius: '12px', padding: '10px 16px'
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="#C9930A" strokeWidth="1.5"/>
              <path d="M8 21h8M12 17v4" stroke="#C9930A" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M6 8h12M6 11h8" stroke="#C9930A" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span style={{ color: '#C9930A', fontSize: '13px', fontWeight: 600, letterSpacing: '0.05em', fontFamily: 'Inter, sans-serif' }}>
              CYPRONETWORK
            </span>
          </div>
        </div>

        <h1 style={{
          color: '#F8FAFC', fontSize: '44px', fontWeight: 700,
          letterSpacing: '-0.04em', lineHeight: 1.05,
          fontFamily: 'Inter, sans-serif', marginBottom: '20px'
        }}>
          DataBank<br />
          <span style={{ color: '#C9930A' }}>Pro</span>
          <span style={{ color: 'rgba(248,250,252,0.3)', fontSize: '32px' }}> v3</span>
        </h1>

        <p style={{ color: 'rgba(248,250,252,0.45)', fontSize: '15px', lineHeight: 1.65, maxWidth: '340px' }}>
          Κεντρική πλατφόρμα διαχείρισης επαφών και επικοινωνίας για την CyproNetwork Consultancy Group.
        </p>

        {/* Stats pills */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '44px', flexWrap: 'wrap' }}>
          {[['2.928', 'Επαφές'],['7', 'Πηγές'],['4', 'Αποστολείς']].map(([n, l]) => (
            <div key={l} style={{
              background: 'rgba(248,250,252,0.04)', border: '1px solid rgba(248,250,252,0.08)',
              borderRadius: '8px', padding: '10px 16px'
            }}>
              <div style={{ color: '#F8FAFC', fontSize: '20px', fontWeight: 700, fontFamily: 'Inter, sans-serif', letterSpacing: '-0.03em' }}>{n}</div>
              <div style={{ color: 'rgba(248,250,252,0.35)', fontSize: '11px', marginTop: '1px' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — login form */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 32px', position: 'relative'
      }}>
        <div style={{
          width: '100%', maxWidth: '380px',
          background: 'rgba(248,250,252,0.04)',
          border: '1px solid rgba(248,250,252,0.08)',
          borderRadius: '20px', padding: '36px',
          backdropFilter: 'blur(24px)'
        }}>
          <div style={{ marginBottom: '28px' }}>
            <h2 style={{ color: '#F8FAFC', fontSize: '22px', fontWeight: 700, letterSpacing: '-0.03em', fontFamily: 'Inter, sans-serif' }}>
              Σύνδεση
            </h2>
            <p style={{ color: 'rgba(248,250,252,0.35)', fontSize: '13px', marginTop: '6px' }}>
              Εισάγετε τα στοιχεία πρόσβασής σας
            </p>
          </div>

          <form onSubmit={submit}>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'rgba(248,250,252,0.5)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Username
              </label>
              <input
                style={{
                  width: '100%', padding: '10px 13px', borderRadius: '8px',
                  border: '1px solid rgba(248,250,252,0.12)',
                  background: 'rgba(248,250,252,0.06)', color: '#F8FAFC',
                  fontSize: '14px', fontFamily: 'inherit', outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                placeholder="username"
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                autoFocus required
                onFocus={e => e.target.style.borderColor = 'rgba(201,147,10,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(248,250,252,0.12)'}
              />
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'rgba(248,250,252,0.5)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Password
              </label>
              <input
                style={{
                  width: '100%', padding: '10px 40px 10px 13px', borderRadius: '8px',
                  border: '1px solid rgba(248,250,252,0.12)',
                  background: 'rgba(248,250,252,0.06)', color: '#F8FAFC',
                  fontSize: '14px', fontFamily: 'inherit', outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required
                onFocus={e => e.target.style.borderColor = 'rgba(201,147,10,0.6)'}
                onBlur={e => e.target.style.borderColor = 'rgba(248,250,252,0.12)'}
              />
              <button type="button" onClick={() => setShowPass(!showPass)} style={{
                position: 'absolute', right: '12px', bottom: '10px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(248,250,252,0.3)', fontSize: '12px'
              }}>
                {showPass ? '🙈' : '👁'}
              </button>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', marginTop: '8px', padding: '11px',
              borderRadius: '8px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              background: loading ? 'rgba(201,147,10,0.5)' : 'linear-gradient(135deg, #C9930A, #F5C842)',
              color: '#0A0F1E', fontSize: '14px', fontWeight: 700,
              fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em',
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
              {loading ? <><span className="spin" style={{ borderTopColor: '#0A0F1E', borderColor: 'rgba(10,15,30,0.2)' }} />Σύνδεση...</> : 'Σύνδεση →'}
            </button>
          </form>

          <p style={{ textAlign: 'center', color: 'rgba(248,250,252,0.2)', fontSize: '11px', marginTop: '28px' }}>
            CyproNetwork Consultancy Group © 2026
          </p>
        </div>
      </div>
    </div>
  );
}
